import { useMemo, useState } from 'react';
import { VSEPR_PRESETS, buildGenericModel, buildVseprModel, prettyFormula } from '@chemai/engine';
import { type MessageKey, useI18n } from '../../i18n';
import { MoleculeViewer } from '../../components/MoleculeViewer';
import { NumberField, SaveButton, Segmented, SelectField, Stat } from '../../components/ui';
import { progress } from '../../lib/progress';

export function GeometryLab() {
  const { t, num } = useI18n();
  const [mode, setMode] = useState<'preset' | 'custom'>('preset');
  const [presetId, setPresetId] = useState('h2o');
  const [bp, setBp] = useState(3);
  const [lp, setLp] = useState(1);

  const valid = bp >= 2 && bp + lp >= 2 && bp + lp <= 6;
  const model = useMemo(() => {
    if (mode === 'preset') return buildVseprModel(VSEPR_PRESETS.find((p) => p.id === presetId)!);
    return valid ? buildGenericModel(bp, lp) : null;
  }, [mode, presetId, bp, lp, valid]);

  const info = model?.vsepr;
  const shapeName = info ? t(`shape.${info.shape}` as MessageKey) : '';

  return (
    <div className="sim">
      <div className="sim-grid two">
        <section className="panel">
          <Segmented label={t('geo.mode')} value={mode} onChange={setMode} options={[{ value: 'preset', label: t('geo.preset') }, { value: 'custom', label: t('geo.custom') }]} />
          {mode === 'preset' ? (
            <SelectField label={t('geo.molecule')} value={presetId} onChange={setPresetId}
              options={VSEPR_PRESETS.map((p) => ({ value: p.id, label: prettyFormula(p.formula) }))} />
          ) : (
            <>
              <NumberField label={t('geo.bonding')} value={bp} onChange={(v) => setBp(Math.round(v))} min={2} max={6} step={1} slider />
              <NumberField label={t('geo.lonePairs')} value={lp} onChange={(v) => setLp(Math.round(v))} min={0} max={3} step={1} slider />
              {!valid && <p className="error" role="alert">{t('geo.invalid')}</p>}
            </>
          )}
          {info && model && (
            <>
              <div className="stats">
                <Stat label={t('geo.notation')} value={<span className="mono">{info.notation}</span>} emphasis />
                <Stat label={t('geo.shape')} value={shapeName} />
                <Stat label={t('geo.electronGeometry')} value={t(`shape.${info.electronGeometry}` as MessageKey)} />
                <Stat label={t('geo.angle')} value={info.idealAngles.map((a) => `${num(a, 1)}°`).join(', ')} />
                {mode === 'preset' && <Stat label={t('geo.polarity')} value={model.polar ? t('geo.polar') : t('geo.nonpolar')} />}
              </div>
              {info.anglesCompressed && <p className="hint">{t('geo.compressed')}</p>}
              <SaveButton onSave={() => progress.recordExperiment('geometry', { f: mode === 'preset' ? model.formula : info.notation, s: info.shape })} />
            </>
          )}
        </section>
        <section className="panel">
          {model && info && (
            <MoleculeViewer
              model={model}
              description={t('mol.describe', { f: mode === 'preset' ? model.formula : info.notation, n: model.atoms.length, s: shapeName })}
              height={380}
            />
          )}
        </section>
      </div>
    </div>
  );
}
